import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, Plus, X } from 'lucide-react';
import { tutorService } from '../services/tutorService';
import authService from '../services/authService';
import TutorCard from '../components/TutorCard';
import { buildTutorPreviewForCard, previewLocationFromAddForm } from '../utils/tutorPreview';
import { AM_REGIONS as regions, YEREVAN_DISTRICTS as yerevanDistricts } from '../constants/armeniaGeo';
import { ARMENIA_SETTLEMENTS_BY_REGION } from '../constants/armeniaSettlementsByRegion';
import './AddTutor.css';
import './tutorFormPreview.css';

const AddTutor = () => {
  const navigate = useNavigate();
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createdTutorId, setCreatedTutorId] = useState(null); // Store created tutor ID
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    region: '',
    city: '',
    district: '',
    hourlyRate: '',
    subjects: [],
    modes: [], // Changed to array for multi-select
    experience: '',
    description: '',
    additionalInfo: '', // New optional field
    supportsWhatsapp: false,
    supportsViber: false,
    telegramUsername: '',
    photo: null
  });

  // Check authentication on component mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await authService.getCurrentUser();
        if (response.user) {
          setUser(response.user);
          // Prefill email from authenticated user
          setFormData(prev => ({
            ...prev,
            email: response.user.email
          }));
        } else {
          // Not authenticated, redirect to register
          navigate('/register');
        }
      } catch (error) {
        console.error('Auth check error:', error);
        navigate('/register');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [navigate]);

  const [newSubject, setNewSubject] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false); // Collapsible state

  const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null);

  useEffect(() => {
    if (!formData.photo) {
      setPhotoPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(formData.photo);
    setPhotoPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [formData.photo]);

  const previewLocation = useMemo(
    () => previewLocationFromAddForm(formData),
    [formData.region, formData.city, formData.district]
  );

  const previewTutor = useMemo(
    () =>
      buildTutorPreviewForCard({
        id: 0,
        name: formData.name,
        photo: photoPreviewUrl,
        subjects: formData.subjects,
        hourlyRate: formData.hourlyRate,
        experienceYears: formData.experience,
        location: previewLocation,
        modes: formData.modes,
        description: formData.description,
      }),
    [
      formData.name,
      formData.subjects,
      formData.hourlyRate,
      formData.experience,
      formData.modes,
      formData.description,
      previewLocation,
      photoPreviewUrl,
    ]
  );

  const cityOptions =
    formData.region && formData.region !== 'Երևան'
      ? ARMENIA_SETTLEMENTS_BY_REGION[formData.region] || []
      : [];

  const subjects = [
    "Մաթեմատիկա", "Ֆիզիկա", "Քիմիա", "Կենսաբանություն",
    "Անգլերեն", "Ռուսերեն", "Ֆրանսերեն", "Գերմաներեն",
    "Պատմություն", "Աշխարհագրություն", "Տնտեսագիտություն",
    "Հայոց լեզու", "Հայոց պատմություն", "Ինֆորմատիկա",
    "Ծրագրավորում", "Գրականություն", "Երաժշտություն", "Շախմատ"
  ];
  
  const modeOptions = [
    { value: "Online", label: "Օնլայն", icon: "💻" },
    { value: "In-Person", label: "Իր մոտ", icon: "🏠" },
    { value: "Both", label: "Ձեր մոտ", icon: "🚗" }
  ];
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleRegionChange = (e) => {
    setFormData(prev => ({
      ...prev,
      region: e.target.value,
      // City is required by the backend form validation, but for Yerevan we don't need to show the city dropdown.
      // We keep the value defaulted so the saved `location` string remains consistent: "{district}, Երևան".
      city: e.target.value === 'Երևան' ? 'Երևան' : '',
      district: ''
    }));
    if (errors.region) {
      setErrors(prev => ({ ...prev, region: '' }));
    }
  };

  const handleModeToggle = (mode) => {
    setFormData(prev => {
      const modes = prev.modes.includes(mode)
        ? prev.modes.filter(m => m !== mode)
        : [...prev.modes, mode];
      return { ...prev, modes };
    });
    if (errors.modes) {
      setErrors(prev => ({ ...prev, modes: '' }));
    }
  };

  const handleSubjectAdd = () => {
    if (newSubject && !formData.subjects.includes(newSubject)) {
      setFormData(prev => ({
        ...prev,
        subjects: [...prev.subjects, newSubject]
      }));
      setNewSubject('');
      if (errors.subjects) {
        setErrors(prev => ({ ...prev, subjects: '' }));
      }
    }
  };

  const handleSubjectRemove = (subject) => {
    setFormData(prev => ({
      ...prev,
      subjects: prev.subjects.filter(s => s !== subject)
    }));
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, photo: file }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Պարտադիր դաշտ';
    if (!formData.email.trim()) newErrors.email = 'Պարտադիր դաշտ';
    if (!formData.phone.trim()) newErrors.phone = 'Պարտադիր դաշտ';
    if (!formData.region) newErrors.region = 'Պարտադիր դաշտ';
    if (formData.region !== 'Երևան' && !formData.city) newErrors.city = 'Պարտադիր դաշտ';
    if (!formData.hourlyRate) newErrors.hourlyRate = 'Պարտադիր դաշտ';
    if (!formData.experience) newErrors.experience = 'Պարտադիր դաշտ';
    if (formData.subjects.length === 0) newErrors.subjects = 'Ընտրեք առնվազն մեկ ոլորտ';
    if (formData.modes.length === 0) newErrors.modes = 'Ընտրեք առնվազն մեկ ձևաչափ';
    if (!formData.description.trim()) newErrors.description = 'Պարտադիր դաշտ';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitting(true);

    try {
      const isYerevan = formData.region === 'Երևան';

      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('region', formData.region);
      submitData.append('city', isYerevan ? 'Երևան' : formData.city.trim());
      submitData.append('district', isYerevan ? (formData.district || '').trim() : '');
      submitData.append('modes', JSON.stringify(formData.modes)); // Send array of modes
      submitData.append('hourly_rate', formData.hourlyRate);
      submitData.append('experience', formData.experience);
      submitData.append('description', formData.description);
      submitData.append('subjects', JSON.stringify(formData.subjects));
      
      if (formData.additionalInfo) {
        submitData.append('additional_info', formData.additionalInfo);
      }
      
      submitData.append('supports_whatsapp', formData.supportsWhatsapp.toString());
      submitData.append('supports_viber', formData.supportsViber.toString());
      
      if (formData.telegramUsername) {
        submitData.append('telegram_username', formData.telegramUsername.trim().replace('@', ''));
      }
      
      if (formData.photo) {
        submitData.append('photo', formData.photo);
      }

      const result = await tutorService.createTutor(submitData);
      console.log('Submission result:', result);
      
      // Store the created tutor ID
      setCreatedTutorId(result.id);
      
      setSuccess(true);

    } catch (err) {
      console.error('Error:', err);
      setErrors({ submit: 'Սխալ է տեղի ունեցել: Խնդրում ենք փորձել կրկին:' });
    } finally {
      setSubmitting(false);
    }
  };

  // Show loading while checking authentication
  if (loading) {
    return (
      <div className="add-tutor-page">
        <div className="loading" style={{ textAlign: 'center', padding: '3rem', fontSize: '1.2rem', color: '#666' }}>
          Բեռնվում է...
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="add-tutor-page">
        <div className="success-state">
          <div className="success-icon">✓</div>
          <h2>Ձեր դիմումը ընդունվել է և այժմ մշակման փուլում է։</h2>
          <p>
            Արդյունքների մասին կտեղեկանաք {formData.email || (user && user.email) || 'Ձեր նշված'} էլ․ հասցեին։
          </p>
          <div className="success-actions">
            <button 
              className="btn-primary"
              onClick={() => navigate('/')}
            >
              Գլխավոր էջ
            </button>
            {createdTutorId && (
              <button 
                className="btn-secondary"
                onClick={() => navigate('/dashboard')}
              >
                Դիտել իմ պրոֆիլը
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="add-tutor-page">
      <div className="form-container">
        <div className="form-header">
          <h1>Դառնալ մասնագետ</h1>
          <p>Լրացրեք ձևը՝ հարթակում ներկայացվելու համար</p>
        </div>

        <form onSubmit={handleSubmit} className="tutor-form">
          <div className="form-grid">
            <div className="form-field">
              <label>Անուն *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Անուն Ազգանուն"
                className={errors.name ? 'error' : ''}
              />
              {errors.name && <span className="error-text">{errors.name}</span>}
            </div>

            <div className="form-field">
              <label>Էլ. փոստ *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="email@example.com"
                className={errors.email ? 'error' : ''}
                readOnly={!!user}
                disabled={!!user}
              />
              {errors.email && <span className="error-text">{errors.email}</span>}
            </div>

            <div className="form-field">
              <label>Հեռախոսահամար *</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="+374 XX XXX XXX"
                className={errors.phone ? 'error' : ''}
              />
              {errors.phone && <span className="error-text">{errors.phone}</span>}
            </div>
          </div>

          {/* Messaging Contact Section */}
          <div className="form-field-full messaging-section">
            <label>Կապի նախընտրելի եղանակներ (ոչ պարտադիր)</label>
            <div className="messaging-options">
              <label className="checkbox-option">
                <input
                  type="checkbox"
                  checked={formData.supportsWhatsapp}
                  onChange={(e) => setFormData(prev => ({ ...prev, supportsWhatsapp: e.target.checked }))}
                />
                <span>WhatsApp</span>
              </label>
              <label className="checkbox-option">
                <input
                  type="checkbox"
                  checked={formData.supportsViber}
                  onChange={(e) => setFormData(prev => ({ ...prev, supportsViber: e.target.checked }))}
                />
                <span>Viber</span>
              </label>
            </div>
            <div className="form-field telegram-field">
              <label>Telegram օգտանուն (ոչ պարտադիր)</label>
              <input
                type="text"
                name="telegramUsername"
                value={formData.telegramUsername}
                onChange={handleInputChange}
                placeholder="օր․ username (առանց @)"
              />
            </div>
          </div>

          <div className="form-grid">
            <div className="form-field">
              <label>Մարզ *</label>
              <select
                name="region"
                value={formData.region}
                onChange={handleRegionChange}
                className={errors.region ? 'error' : ''}
              >
                <option value="">Ընտրել</option>
                {regions.map(region => (
                  <option key={region} value={region}>{region}</option>
                ))}
              </select>
              {errors.region && <span className="error-text">{errors.region}</span>}
            </div>

            {formData.region && formData.region !== 'Երևան' && (
              <div className="form-field">
                <label>Քաղաք / Գյուղ *</label>
                <select
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className={errors.city ? 'error' : ''}
                >
                  <option value="">Ընտրել քաղաք / գյուղ</option>
                  {cityOptions.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                {errors.city && <span className="error-text">{errors.city}</span>}
              </div>
            )}

            {formData.region === 'Երևան' && (
              <div className="form-field">
                <label>Թաղամաս</label>
                <select
                  name="district"
                  value={formData.district}
                  onChange={handleInputChange}
                >
                  <option value="">Ընտրել</option>
                  {yerevanDistricts.map(district => (
                    <option key={district} value={district}>{district}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-field">
              <label>Գին (դրամ/ժամ) *</label>
              <input
                type="number"
                name="hourlyRate"
                value={formData.hourlyRate}
                onChange={handleInputChange}
                min="0"
                step="100"
                placeholder="5000"
                className={errors.hourlyRate ? 'error' : ''}
              />
              {errors.hourlyRate && <span className="error-text">{errors.hourlyRate}</span>}
            </div>

            <div className="form-field">
              <label>Փորձ (տարի) *</label>
              <input
                type="number"
                name="experience"
                value={formData.experience}
                onChange={handleInputChange}
                min="0"
                step="1"
                placeholder="3"
                className={errors.experience ? 'error' : ''}
              />
              {errors.experience && <span className="error-text">{errors.experience}</span>}
            </div>
          </div>

          <div className="form-field-full">
            <label>Ոլորտներ *</label>
            <div className="subject-selector">
              <div className="subject-input">
                <select
                  value={newSubject}
                  onChange={(e) => setNewSubject(e.target.value)}
                >
                  <option value="">Ընտրել ոլորտ</option>
                  {subjects.filter(s => !formData.subjects.includes(s)).map(subject => (
                    <option key={subject} value={subject}>{subject}</option>
                  ))}
                </select>
                <button type="button" onClick={handleSubjectAdd} disabled={!newSubject}>
                  <Plus size={18} />
                </button>
              </div>
              {formData.subjects.length > 0 && (
                <div className="subject-tags">
                  {formData.subjects.map(subject => (
                    <div key={subject} className="subject-tag">
                      <span>{subject}</span>
                      <button type="button" onClick={() => handleSubjectRemove(subject)}>
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {errors.subjects && <span className="error-text">{errors.subjects}</span>}
          </div>

          <div className="form-field-full">
            <label>Անցկացման ձև * (կարող եք ընտրել մի քանիսը)</label>
            <div className="mode-selector">
              {modeOptions.map(mode => (
                <button
                  key={mode.value}
                  type="button"
                  className={`mode-option ${formData.modes.includes(mode.value) ? 'selected' : ''}`}
                  onClick={() => handleModeToggle(mode.value)}
                >
                  <span className="mode-icon">{mode.icon}</span>
                  <span>{mode.label}</span>
                </button>
              ))}
            </div>
            {errors.modes && <span className="error-text">{errors.modes}</span>}
          </div>

          <div className="form-field-full">
            <label>Կարճ նկարագրություն *</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="3"
              placeholder="Գրեք Ձեր մասին և փորձի մասին..."
              className={errors.description ? 'error' : ''}
            />
            {errors.description && <span className="error-text">{errors.description}</span>}
          </div>

          {/* Collapsible Additional Info Section */}
          <div className="form-field-full additional-info-section">
            {!showAdditionalInfo ? (
              <button
                type="button"
                className="toggle-additional-info"
                onClick={() => setShowAdditionalInfo(true)}
              >
                <Plus size={16} />
                <span>Ավելացնել կրթություն կամ սերտիֆիկատներ</span>
              </button>
            ) : (
              <div className="additional-info-expanded">
                <div className="additional-info-header">
                  <label>Լրացուցիչ տեղեկություն (ոչ պարտադիր)</label>
                  <button
                    type="button"
                    className="close-additional-info"
                    onClick={() => {
                      setShowAdditionalInfo(false);
                      setFormData(prev => ({ ...prev, additionalInfo: '' }));
                    }}
                  >
                    <X size={16} />
                  </button>
                </div>
                <textarea
                  name="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={handleInputChange}
                  rows="4"
                  placeholder="Օրինակ՝ ԵՊՀ մագիստրոս, IELTS 8.0, միջազգային սերտիֆիկատներ, մրցանակներ և այլն…"
                />
              </div>
            )}
          </div>

          <div className="form-field-full">
            <label>Լուսանկար</label>
            <div className="photo-upload">
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                id="photo-upload"
              />
              <label htmlFor="photo-upload" className="upload-button">
                <Upload size={18} />
                <span>{formData.photo ? formData.photo.name : 'Ընտրել նկար'}</span>
              </label>
            </div>
          </div>

          {errors.submit && (
            <div className="submit-error">
              {errors.submit}
            </div>
          )}

          <button
            type="submit"
            className="submit-button"
            disabled={submitting}
          >
            {submitting ? 'Ուղարկվում է...' : 'Ուղարկել հաստատման'}
          </button>
        </form>

        <section className="tutor-form-preview" aria-labelledby="add-tutor-preview-title">
          <h2 id="add-tutor-preview-title" className="tutor-form-preview-title">
            Նախադիտում
          </h2>
          <p className="tutor-form-preview-hint">
            Այսպես կերևա ձեր քարտը կայքում հաստատումից հետո։
          </p>
          <div className="tutor-form-preview-body">
            <TutorCard tutor={previewTutor} preview />
          </div>
        </section>
      </div>
    </div>
  );
};

export default AddTutor;
